import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Divider, Wrapper, FilterContainer } from './styles'
import MultiSelect from '~/components/MultiSelect'
import { Button } from 'reactstrap'
import { brazilianStates } from '~/constants/location'
import { useFormik } from 'formik'
import {
  getFilteredAreaOptionsWithStateAndCity,
  getFilteredCityOptionsWithStateAndArea,
  getFilteredStateOptionsWithCityAndArea,
} from '~/helpers/getFilteredOptions'
import { useFormFilters } from '~/hooks/useFormFilters'
import { useSearchParamsHandler } from '~/contexts/FilterContext'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import { toast } from 'react-toastify'

function DefaultFiltersForm({ onClose }) {
  const { query, setQueries } = useSearchParamsHandler()
  const defaultFilters = {
    states: query.states ?? [],
    cities: query.cities ?? [],
    areas: query.areas ?? [],
    labels: query.labels ?? [],
    locations: query.locations ?? [],
  }

  const filterCount = getDefaultFiltersCount(defaultFilters)
  const formik = useFormik({
    initialValues: defaultFilters,
    onSubmit: values => {
      setQueries({ ...values, page: 1 })
      toast(intl.formatMessage({ id: 'Filters added successfully' }), {
        position: 'top-center',
        type: 'success',
      })
      onClose()
    },
  })

  const intl = useIntl()

  const clearFilter = () => {
    setQueries({
      states: [],
      cities: [],
      areas: [],
      labels: [],
      locations: [],
      page: 1,
    })
    onClose()
  }

  const {
    states,
    cities,
    areas,
    labels,
    locations,
    handleFilters,
    filterLoading,
    filters,
  } = useFormFilters({ values: formik.values })

  return (
    <Wrapper>
      <FilterContainer>
        <h2 className="title">
          <FormattedMessage id="Locations" />
        </h2>
        <div className="content__wrapper">
          <MultiSelect
            uniqueKey="stateFilter"
            value={states}
            onChange={v => {
              handleFilters(v, 'state', val =>
                formik.setFieldValue('states', val)
              )
            }}
            placeholder={intl.formatMessage({ id: 'Select a state' })}
            options={filters?.states
              .filter(el =>
                getFilteredStateOptionsWithCityAndArea(el, cities, areas)
              )
              ?.map(state => {
                return {
                  value: [state],
                  label: brazilianStates.find(item => item.uf === state)?.state,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="cityFilter"
            value={cities}
            onChange={v =>
              handleFilters(v, 'city', val =>
                formik.setFieldValue('cities', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select a city' })}
            options={filters?.cities
              ?.filter(item =>
                getFilteredCityOptionsWithStateAndArea(item, states, areas)
              )
              ?.map(city => {
                return {
                  value: [city],
                  label: city,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="areaFilter"
            value={areas}
            onChange={v =>
              handleFilters(v, 'area', val =>
                formik.setFieldValue('areas', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select an area' })}
            options={filters?.areas
              ?.filter(item =>
                getFilteredAreaOptionsWithStateAndCity(item, states, cities)
              )
              ?.map(area => {
                return {
                  value: [area],
                  label: area,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="labelFilter"
            value={labels}
            onChange={v =>
              handleFilters(v, 'label', val =>
                formik.setFieldValue('labels', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select a label' })}
            options={filters?.labels?.map(label => {
              return {
                value: [label],
                label: label,
              }
            })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="locationFilter"
            value={locations}
            onChange={v =>
              handleFilters(v, 'location', val => {
                formik.setFieldValue('locations', val)
              })
            }
            placeholder={intl.formatMessage({ id: 'Select a location' })}
            options={filters?.locations?.map(location => {
              return {
                value: [location.id],
                label: location,
              }
            })}
            renderOption={({ label: location }) => (
              <div className="col">
                <span>{location?.name ?? ''}</span>
                <small style={{ display: 'block', color: '#8D8D8D' }}>
                  {location?.data?.storefrontAddress?.addressLines?.join(
                    ', '
                  ) ?? ''}
                </small>
                <small style={{ display: 'block', color: '#8D8D8D' }}>
                  Store code: {location?.data?.storeCode ?? ''}
                </small>
              </div>
            )}
            loading={filterLoading}
          />
        </div>
      </FilterContainer>
      <Divider />
      <div className="modal-footer__variant">
        <Button
          color="primary"
          onClick={() => formik.handleSubmit()}
          style={{ width: '100%' }}
          disabled={!formik.dirty}
        >
          <FormattedMessage id="Add filters" />
        </Button>
        <Button
          color="secondary"
          outline
          onClick={clearFilter}
          style={{ width: '100%' }}
          disabled={filterCount === 0}
        >
          <FormattedMessage id="Clear filter" />
        </Button>
      </div>
    </Wrapper>
  )
}
export default DefaultFiltersForm
